'use client';

import type { ReportItemProps } from '@/@studio-ai/props';
import { formatRelativeDate } from '@/lib/utils/formatting';
import { Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { ReportItemMenu } from './report-item-menu';

export function ReportItem({
    report,
    isActive = false,
    onSelect,
    onDelete,
    onRename,
    onShare,
    folders,
    onMoveToFolder,
    isLoading = false,
    className = '',
}: ReportItemProps) {
    const title = report.title || `Report ${report.id}`;

    return (
        <div
            className={`group/row flex items-center gap-3 px-4 py-2.5 transition-colors ${
                isActive
                    ? 'bg-default-100'
                    : isLoading
                      ? 'cursor-not-allowed opacity-50'
                      : 'hover:bg-default-50'
            } ${className}`}
        >
            <Icon
                icon="lucide:grip-vertical"
                width={12}
                className="shrink-0 cursor-grab text-default-300"
            />
            <div
                role="button"
                tabIndex={isLoading ? -1 : 0}
                aria-disabled={isLoading}
                className={`flex min-w-0 flex-1 items-center gap-2 rounded outline-none focus-visible:ring-2 focus-visible:ring-primary ${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={() => !isLoading && onSelect()}
                onKeyDown={(e) => {
                    if (!isLoading && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onSelect();
                    }
                }}
            >
                {report.masqueradeOrgName ? (
                    <Tooltip content={`Masquerading as ${report.masqueradeOrgName}`}>
                        <Icon
                            icon="lucide:venetian-mask"
                            width={14}
                            className="shrink-0 text-primary"
                        />
                    </Tooltip>
                ) : (
                    <Icon
                        icon="lucide:file-text"
                        width={14}
                        className="shrink-0 text-default-400"
                    />
                )}

                <span className="truncate text-small text-foreground">{title}</span>
            </div>
            <span className="shrink-0 text-mini text-default-400">
                {formatRelativeDate(report.createdTimestamp)}
            </span>
            <div className="w-8 shrink-0">
                <div className="flex justify-end transition-opacity">
                    <ReportItemMenu
                        report={report}
                        folders={folders ?? []}
                        onOpen={onSelect}
                        onRename={onRename}
                        onDelete={onDelete}
                        onShare={onShare ?? (() => {})}
                        onMoveToFolder={onMoveToFolder ?? (() => {})}
                    />
                </div>
            </div>
        </div>
    );
}
